import { isPlatformBrowser } from '@angular/common';
import { Component, ElementRef, Inject, PLATFORM_ID, ViewChild, ViewEncapsulation } from '@angular/core';
import { ReferenceConst } from '@core-constants/reference.const';
import { SettingsManager } from '@core-managers/settings.manager';
import { Environment } from '@environments';
import { TranslateService } from '@shared-services/translate.service';
import { CookieService } from 'ngx-cookie-service';
import { LocalStorageConst, SesionStorageConst } from '@core-constants/storage.const';
import { EAppEventName } from '@core-constants/app-events.const';
import { BroadcastService } from '@shared-services/broadcast.service';
import { InteractiveToasterService } from '@shared-services/interactive-toast.service';
import { InteractiveToasterConfigurations } from '@core-constants/interactive-toaster.const';
import { TokenManager } from '@core-managers/token.manager';

declare function goToChatBot(): void;
declare function loadChatScripts(): void;
declare const hideModal: any;

@Component({
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.less'],
  encapsulation: ViewEncapsulation.None
})
export class ChatComponent
{
  public chatBtnId: string = ReferenceConst.ChatButtonId;
  public whatsappImage: string = "/icons/Whatsapp_home.svg";
  public whatsappurl = ReferenceConst.WhatsAppUrl;
  public showPrechat: boolean = true;
  private ariaLabelChatButton: string = "Iniciar chat";

  private readonly showChatbotEventId: string = "showChatbot";
  private readonly hideChatbotEventId: string = "hideChatbot";
  private readonly minimizeChatbotEventId: string = "minimizeChatbot";
  private readonly updateCartChatbotEventId: string = "updateCart";

  private readonly chatbotContainerId: string = "chatbot-v2-container";

  private readonly aivoUserCartId: string = "cartId";
  private readonly aivoUserSessionTrackingId: string = "sessionTrackingId";
  private readonly aivoUserSessionId: string = "sessionId";

  @ViewChild(ReferenceConst.ChatButtonId) public buttonChat: ElementRef;

  constructor(@Inject(PLATFORM_ID) private platformId,
    protected translateService: TranslateService,
    public cookieService: CookieService,
    public tokenManager: TokenManager,
    public settingsManager: SettingsManager,
    protected interactiveToast: InteractiveToasterService) { }

  ngAfterViewInit(): void
  {
    if (isPlatformBrowser(this.platformId))
    {
      this.detectChatbotChanges();
    }
  }

  private detectChatbotChanges(): void
  {
    if (document.readyState === 'loading')
    {
      document.addEventListener('DOMContentLoaded', this.contentLoadedListener);
    }
    else
    {
      this.contentLoadedListener(null);
    }
  }

  private contentLoadedListener(event): void
  {
    window.addEventListener('message', this.iframeMessageListener);
  }

  private iframeMessageListener = (event: MessageEvent): void =>
  {
    const chatbotContainer = document.getElementById(this.chatbotContainerId);

    switch (event.data)
    {
      case this.showChatbotEventId:
        chatbotContainer.classList.remove('chatbotMaximized', 'chatbotHidden', 'chatbotMinimized');
        chatbotContainer.classList.add('chatbotMaximized');
        break;
      case this.hideChatbotEventId:
        chatbotContainer.classList.remove('chatbotMaximized', 'chatbotHidden', 'chatbotMinimized');
        chatbotContainer.classList.add('chatbotHidden');
        break;
      case this.minimizeChatbotEventId:
        chatbotContainer.classList.remove('chatbotMaximized', 'chatbotHidden', 'chatbotMinimized');
        chatbotContainer.classList.add('chatbotMinimized');
        break;
      case this.updateCartChatbotEventId:
        BroadcastService.Instance.broadcast(EAppEventName.OnAddToCart, this.getAivoUser().get().cartId);
        this.interactiveToast.showMessage(InteractiveToasterConfigurations.GoToCart);
        break;
    }
  };

  public ngAfterViewChecked(): void
  {
    if (!isPlatformBrowser(this.platformId))
    {
      return;
    }

    const aivoUser = this.getAivoUser();

    if (!aivoUser)
    {
      return;
    }

    this.setCartCookie(aivoUser);
    this.setSessionCookie(aivoUser);
  }

  private getAivoUser(): any
  {
    const chatbotIframe = document.getElementById(this.chatbotContainerId) as HTMLIFrameElement;
    return (chatbotIframe?.contentWindow as any)?.$aivo?.user ?? null;
  }

  private setCartCookie(aivoUser): void
  {
    let cartCookie = this.cookieService.get(SesionStorageConst.CartCookieKey);

    if (cartCookie)
    {
      cartCookie = cartCookie.replace(/^"(.*)"$/, '$1');

      if (cartCookie !== aivoUser.get().cartId)
      {
        aivoUser.set(this.aivoUserCartId, cartCookie);
      }
    }
    else if (aivoUser.get().cartId) 
    {
      aivoUser.set(this.aivoUserCartId, null);
    }
  }

  private setSessionCookie(aivoUser): void
  {
    const sessionCookie = this.cookieService.get(SesionStorageConst.ExtranetSessionCookieKey);

    if (sessionCookie)
    {
      const sessionCookieParts = sessionCookie.split('-');
      const sessionTrackingId = sessionCookieParts[0];
      const sessionId = sessionCookieParts[1];

      if (sessionCookieParts.length === 2 && sessionTrackingId !== aivoUser.get().sessionTrackingId && sessionId !== aivoUser.get().sessionId)
      {
        aivoUser.set(this.aivoUserSessionTrackingId, sessionTrackingId);
        aivoUser.set(this.aivoUserSessionId, sessionId);

      }
    }
    else if (aivoUser.get().sessionId) 
    {
      aivoUser.set(this.aivoUserSessionTrackingId, null);
      aivoUser.set(this.aivoUserSessionId, null);
    }
  }

  public get isChatEnabled(): boolean
  {
    return this.settingsManager.isChatIconEnabled === true;
  }

  public get isAivoChatbotEnabled(): boolean
  {
    return this.settingsManager.isAivoChatbotEnabled === true;
  }

  public get isWhatsAppEnabled(): boolean
  {
    return this.settingsManager.isWhatsAppIconEnabled === true;
  }

  public get environmentChat(): boolean
  {
    return Environment.ChatBot;
  }

  public get urlSalesForce(): string
  {
    return Environment.UrlSalesForce;
  }

  public get urlChatBot(): string
  {
    return Environment.UrlChatBot;
  }

  public get urlAvatarChatBot(): string
  {
    return Environment.UrlAvatarChatBot;
  }

  public get baseLiveAgentContentURL(): string
  {
    return Environment.BaseLiveAgentContentURL;
  }

  public get salesForceId(): string
  {
    return Environment.SalesForceId;
  }

  public get deploymentId(): string
  {
    return Environment.DeploymentId;
  }

  public get buttonId(): string
  {
    return Environment.ButtonId;
  }

  public get baseLiveAgentURL(): string
  {
    return Environment.BaseLiveAgentURL;
  }

  public get eswLiveAgentDevName(): string
  {
    return Environment.EswLiveAgentDevName;
  }

  public get ariaLabelForChatButton(): string
  {
    return this.translateService.getElement(this.ariaLabelChatButton);
  }

  public get whatsAppUrl(): string
  {
    return this.tokenManager.getUser()?.isAkkyPro ? ReferenceConst.WhatsAppUrlAkkyPro : ReferenceConst.WhatsAppUrl;
  }

  public openChat(): void
  {
    if (isPlatformBrowser(this.platformId))
    {
      if (this.environmentChat)
      {
        goToChatBot();
      }
      else
      {
        loadChatScripts();
      }
    }
  }

  public onClosePrechat(): void
  {
    hideModal();
    this.buttonChat.nativeElement.style.display = "block";
  }
}
